/**
 * File: apps/qbench-cp/reactapp/src/shared/utils/fields.js
 *
 * Purpose: Field-configuration lookup, async select-option loaders, entity field sorting/
 *     display rendering, and entity-relationship metadata for the customer portal's dynamic
 *     forms and tables.
 *
 * Scope: Additional-field configuration, react-select async loaders for relationship fields
 *     (turnaround, project, source, customer, contact, user, etc.), and field-driven display
 *     rendering for orders/samples/tests.
 *
 * Tags: customer-portal, field-config
 *
 * Overview: `getFieldConfig` reads cached field configuration out of sessionStorage per data
 *     type/attribute. A family of `*Select` objects (TurnaroundSelect, ProjectSelect,
 *     AccessioningTypeSelect, SourceSelect, CustomerSelect, ContactSelect, UserSelect,
 *     TestStatuses) each provide `loadOptions`/`getOptionLabel`/`getOptionValue`/
 *     `getDefaultValue` for react-select-async-paginate, backed by the shared
 *     `asyncLoadOptions` pager. `Meta` maps each `EntityType` to its relationship-field
 *     select configs and its field-config getter. `parseFieldConfig` normalizes a raw field
 *     config payload — sorting fields by portal order, filtering hidden fields, and special-
 *     casing panel and portal-view report config. `sortEntityBy`/`renderDisplayField`/
 *     `testResultsRender` handle sorting table rows by field type and rendering a field's
 *     display value (including object-relation, tag, hyperlink, and worksheet-driven test
 *     result values). `entityRefMap` provides a second, order/sample/test-scoped map of
 *     relationship-field option accessors, some marked "keep for backward compatibility
 *     until V1 is deprecated."
 *
 * Dependencies: sessionStorage key "fieldConfigs" populated elsewhere in the app; shared/
 *     configs/rbac/accessPolicies for creatable-relationship policies
 *
 * Exports: getFieldConfig, convertDateFromServer, AddlFieldSelect, TurnaroundSelect,
 *     ProjectSelect, AccessioningTypeSelect, SourceSelect, CustomerSelect, ContactSelect,
 *     UserSelect, TestStatuses, Meta, parseFieldConfig, sortEntityBy, cutIgnoredItems,
 *     renderDisplayField, testResultsRender, CreateOrderCommonContentToString, entityRefMap
 */

import { useAppStore } from 'App/store';
import { useOrderRequest } from 'context/orderRequest';
import pick from 'lodash/pick';
import { DateTime } from 'luxon';
import policies from 'shared/configs/rbac/accessPolicies';
import { EntityType } from 'shared/constants/entityTypes';
import {
  AddlFieldType,
  ColumnSort,
  PORTALVIEW_PERMISSION,
} from 'shared/constants/fields';
import { useProjectCreate } from 'shared/hooks/api/projects';
import { useSourceUpsert } from 'shared/hooks/api/sources';
import styled from 'styled-components';
import { logError } from 'shared/utils/logger';
import { axiosinstance } from './api';
import { parseObjRelDefault } from './form';
import { isNotEmpty, safeJsonParse } from './functions';

export const getFieldConfig = (dataType, attribute = null) => {
  const res = safeJsonParse(sessionStorage.getItem('fieldConfigs'));
  if (isNotEmpty(res)) {
    const fieldConfig = res[dataType];
    return attribute
      ? fieldConfig.find((item) => item.name === attribute)
      : fieldConfig;
  }
  return;
};

export const convertDateFromServer = (date) => {
  return DateTime.fromFormat(date, 'MM/dd/yyyy hh:mm a', { zone: 'UTC' })
    .toLocal()
    .toFormat('MM/dd/yyyy hh:mm a');
};

/* ~~~~START Async Select ~~~~*/
const asyncLoadOptions = async (url, search, prevOptions, page) => {
  try {
    const delimiter = url.includes('?') ? '&' : '?';
    const queryString = `search=${search}&page=${page}`;
    const { data, total_pages, page_number } = await axiosinstance
      .get(`${url}${delimiter}${queryString}`)
      .then((res) => res.data);
    // hasMore is a named local rather than an inline `page_number < total_pages` inside the
    // returned object literal on purpose: lizard@1.23.0's JSX-aware tokenizer misreads a bare
    // `<` comparison embedded in an object-literal value position as the start of a tag and
    // loses track of this function's closing brace, silently merging every function after it
    // in the file into asyncLoadOptions's own CCN/NLOC count (confirmed: this pre-existing
    // mismeasurement sits at exactly CCN 10 -- one point below the gate's threshold -- and how
    // much code follows this function in the file affects that count, since the mismeasurement
    // keeps merging functions in). Not a behavior change, and not a workaround for a real
    // finding -- asyncLoadOptions carries no nesting or CCN violation of its own.
    const hasMore = page_number < total_pages;
    return {
      options: data,
      hasMore,
      additional: {
        page: page + 1,
      },
    };
  } catch (err) {
    logError('asyncLoadOptions', err);
  }
};
export const AddlFieldSelect = (options) => {
  const loadOptions = async (search, prevOptions, { page }) => {
    let filteredOptions;
    if (!search) {
      filteredOptions = options;
    } else {
      const searchLower = search.toLowerCase();

      filteredOptions = options.filter(({ label }) =>
        label.toLowerCase().includes(searchLower),
      );
    }

    const hasMore = filteredOptions.length > prevOptions.length + 10;
    const slicedOptions = filteredOptions.slice(
      prevOptions.length,
      prevOptions.length + 10,
    );

    return {
      options: slicedOptions,
      hasMore,
    };
  };

  const getOptionLabel = (obj) => obj.label;
  const getOptionValue = (obj) => obj.value;
  const getDefaultValue = (obj) => {
    if (obj) {
      if (typeof obj === 'string') {
        return { label: obj, value: obj };
      } else if (Array.isArray(obj)) {
        return obj.map((e) => ({ label: e, value: e }));
      }
    }
    return null;
  };

  return { loadOptions, getOptionLabel, getOptionValue, getDefaultValue };
};
export const TurnaroundSelect = () => {
  const {
    wip: { assayToAdd: assayId },
  } = useOrderRequest();
  const {
    appState: { filterTATByAssayEnabled },
  } = useAppStore();
  const url =
    filterTATByAssayEnabled && assayId
      ? `/v1/turnarounds?assay_ids=${assayId}`
      : '/v1/turnarounds';
  return {
    loadOptions: async (search, prevOptions, { page }) => {
      const options = await asyncLoadOptions(url, search, prevOptions, page);
      if (page == 1) {
        options.options.unshift({ id: null, name: 'Standard' });
      }

      return options;
    },
    getOptionLabel: (obj) => obj.name,
    getOptionValue: (obj) => obj.id,
    getDefaultValue: (obj) =>
      obj ? { id: obj.id, name: obj.name } : { id: null, name: 'Standard' },
  };
};
export const ProjectSelect = {
  loadOptions: async (search, prevOptions, { page }) =>
    asyncLoadOptions('/v1/projects', search, prevOptions, page),
  getOptionLabel: (obj) => obj.title,
  getOptionValue: (obj) => obj.id,
  getDefaultValue: (obj) => (obj ? { id: obj.id, title: obj.value } : null),
};
export const AccessioningTypeSelect = {
  loadOptions: async (search, prevOptions, { page }) =>
    asyncLoadOptions('/v1/accessioningtypes', search, prevOptions, page),
  getOptionLabel: (obj) => obj.value,
  getOptionValue: (obj) => obj.id,
  getDefaultValue: (obj) => (obj ? { id: obj.id, value: obj.value } : null),
};
export const SourceSelect = {
  loadOptions: async (search, prevOptions, { page }) =>
    asyncLoadOptions('/v1/sources', search, prevOptions, page),
  getOptionLabel: (obj) => obj.display_name,
  getOptionValue: (obj) => obj.id,
  getDefaultValue: (obj) =>
    obj ? { id: obj.id, value: obj.display_name } : null,
};
export const CustomerSelect = {
  loadOptions: async (search, prevOptions, { page }) =>
    asyncLoadOptions('/v1/customers', search, prevOptions, page),
  getOptionLabel: (obj) => {
    let customer_name =
      obj?.customer_account?._display_fields_dict?.plain_text_value;
    return customer_name || obj.customer_name;
  },
  getOptionValue: (obj) => {
    return obj.id || obj.customer_account_id;
  },
  getDefaultValue: (obj) => {
    if (Array.isArray(obj)) {
      let val = [];
      for (let i = 0; i < obj.length; i++) {
        val.push({ id: obj.id, value: obj.customer_name });
      }
      return val;
    } else if (obj) {
      return { id: obj.id, value: obj.customer_name };
    }
    return null;
  },
};

export const ContactSelect = {
  loadOptions: async (search, prevOptions, { page }) =>
    asyncLoadOptions('/v1/contacts', search, prevOptions, page),
  getOptionLabel: (obj) => obj._display_fields_dict?.plain_text_value,
  getOptionValue: (obj) => obj.id,
  getDefaultValue: (obj) => {
    if (Array.isArray(obj)) {
      let val = [];
      for (let i = 0; i < obj.length; i++) {
        val.push({
          id: obj.id,
          value: obj._display_fields_dict?.plain_text_value,
        });
      }
      return val;
    } else if (obj) {
      return { id: obj.id, value: obj._display_fields_dict?.plain_text_value };
    }
    return null;
  },
};

export const UserSelect = {
  loadOptions: async (search, prevOptions, { page }) =>
    asyncLoadOptions('/v1/users', search, prevOptions, page),
  getOptionLabel: (obj) =>
    obj?._display_fields_dict?.plain_text_value || obj?.value || obj?.label,
  getOptionValue: (obj) => obj?.id || obj?.value,
  getDefaultValue: (obj) => {
    if (obj) {
      if (obj?.id) {
        return {
          id: obj.id,
          value: obj._display_fields_dict?.plain_text_value || obj.value,
        };
      } else if (obj?.label) {
        return { id: obj.value, value: obj?.label };
      }
    }
    return null;
  },
};

export const TestStatuses = () => {
  const { appState } = useAppStore();
  const { name: _, ...testStatuses } = appState.fieldConfigs?.STATUSES.find(
    (s) => s.name === EntityType.TEST,
  );
  const subStatusIndent = Array(4).join('\u00A0');
  const options = Object.entries(testStatuses)
    .map(([name, values]) => ({ name, ...values }))
    .sort((a, b) => a.sort_order - b.sort_order)
    .reduce((statuses, status) => {
      const subStatuses = status.children.map((c) => ({
        value: c.name,
        label: `${subStatusIndent}${c.name}`,
      }));
      return [
        ...statuses,
        { value: status.name, label: status.name },
        ...subStatuses,
      ];
    }, []);
  return {
    loadOptions: async () => ({ options }),
    getOptionLabel: (obj) => obj.label,
    getOptionValue: (obj) => obj.value,
    getDefaultValue: (obj) =>
      obj ? { value: obj.value, label: obj.label } : null,
  };
};

/* ~~~~END Async Select ~~~~*/

export const Meta = {
  [EntityType.ORDER]: {
    relationshipFields: {
      turnaround: TurnaroundSelect,
      project: ProjectSelect,
      additional_customers: CustomerSelect,
    },
    getFieldConfig: () => getFieldConfig(EntityType.ORDER),
  },
  [EntityType.SAMPLE]: {
    relationshipFields: {
      accessioning_type: AccessioningTypeSelect,
      project: ProjectSelect,
      source: SourceSelect,
    },
    getFieldConfig: () => getFieldConfig(EntityType.SAMPLE),
  },
  [EntityType.TEST]: {
    relationshipFields: {
      assay: {}, // TODO
      sample: {}, // TODO
      turnaround: TurnaroundSelect,
    },
    getFieldConfig: () => getFieldConfig(EntityType.TEST),
  },
  [EntityType.SOURCE]: {
    relationshipFields: {
      project: ProjectSelect,
    },
    getFieldConfig: () => getFieldConfig(EntityType.SOURCE),
  },
  [EntityType.CUSTOMER]: {
    relationshipFields: {},
    getFieldConfig: () => getFieldConfig(EntityType.CUSTOMER),
  },
  [EntityType.CONTACT]: {
    relationshipFields: {},
    getFieldConfig: () => getFieldConfig(EntityType.CONTACT),
  },
  [EntityType.PROJECT]: {
    relationshipFields: {},
    getFieldConfig: () => getFieldConfig(EntityType.PROJECT),
  },
};

// Sort-order comparator for parseFieldConfig's field ordering, extracted to a standalone
// function so its guard clauses don't accumulate nesting depth inside the for/if/.sort() chain
// that used to host it. Same truth table as the original if/else-if/else-if/else: equal orders
// sort as equal, a NaN order sorts after a numeric one (either side), otherwise numeric compare.
function compareFieldOrder(first, second) {
  if (first === second) return 0;
  if (isNaN(first)) return 1;
  if (isNaN(second)) return -1;
  return first > second ? 1 : -1;
}

export const parseFieldConfig = (data) => {
  let fields = {};

  function _parsePanelFieldConfig(config) {
    const { additional_attribute: isAdditional } = config;
    if (!isAdditional) return config;
    return {
      ...config,
      portal_hidden: false,
      portal_global_hidden: false,
    };
  }

  function _parseReportPortalView(portalViewConfig = {}) {
    const { REPORT: reportConfig } = portalViewConfig;
    const pickList = [
      PORTALVIEW_PERMISSION.KEEP_OVERRIDDEN_REPORTS,
      PORTALVIEW_PERMISSION.INDICATE_OVERRIDDEN_REPORTS,
    ];
    return {
      ...portalViewConfig,
      ...pick(reportConfig, pickList),
    };
  }

  for (let key in data) {
    let config = data[key];

    if (
      [
        'ENABLED_MODULES',
        'ENTITY_DISPLAY_NAMES',
        'PORTAL_VIEW',
        'SETTINGS',
      ].includes(key)
    ) {
      fields[key] =
        key === 'PORTAL_VIEW' ? _parseReportPortalView(config) : config;
      continue;
    }

    if (config) {
      const isPanel = key === EntityType.PANEL;
      fields[key] = Object.keys(config)
        .sort((x, y) =>
          compareFieldOrder(
            parseInt(config[x][isPanel ? 'order' : 'portal_order']),
            parseInt(config[y][isPanel ? 'order' : 'portal_order']),
          ),
        )
        .map((key) => {
          return { name: key, ...config[key] };
        })
        .map((config) => (isPanel ? _parsePanelFieldConfig(config) : config))
        .filter(
          (config) =>
            !(
              config.global_hidden ||
              config.hidden ||
              config.portal_global_hidden ||
              config.portal_hidden
            ),
        );
    }
  }
  return fields;
};

export const sortEntityBy = (
  data,
  sortProp,
  direction,
  dataType = AddlFieldType.TEXT,
) => {
  try {
    switch (dataType) {
      case AddlFieldType.TEXT:
        return sortByText(data, sortProp, direction);
      case AddlFieldType.DATETIME:
        return sortByDateTime(data, sortProp, direction);
      case AddlFieldType.NUM:
        return sortByNumber(data, sortProp, direction);
      default:
        return data;
    }
  } catch (err) {
    return data;
  }
};

const sortByText = (data, sortProp, direction) => {
  if (direction === ColumnSort.ASC) {
    return data.sort(function (a, b) {
      return a[sortProp].localeCompare(b[sortProp]);
    });
  }
  if (direction === ColumnSort.DESC) {
    return data.sort(function (a, b) {
      return b[sortProp].localeCompare(a[sortProp]);
    });
  }
  return data;
};

const sortByDateTime = (data, sortProp, direction) => {
  if (direction === ColumnSort.ASC) {
    return data.sort((a, b) => {
      return new Date(a[sortProp]) - new Date(b[sortProp]);
    });
  }
  if (direction === ColumnSort.DESC) {
    return data.sort((a, b) => {
      return new Date(b[sortProp]) - new Date(a[sortProp]);
    });
  }
  return data;
};

const sortByNumber = (data, sortProp, direction) => {
  return data.sort((a, b) => {
    if (direction === ColumnSort.ASC) {
      return a[sortProp] - b[sortProp]; // Ascending
    } else if (direction === ColumnSort.DESC) {
      return b[sortProp] - a[sortProp]; // Descending
    }
    return 0; // No sorting if direction is not recognized
  });
};

export const cutIgnoredItems = (
  list,
  config = [],
  numberOfColumns = undefined,
) => {
  return list
    .filter((item) => {
      return !config.some((c) => item[c.field] === c.rule);
    })
    .filter((_, idx) => (numberOfColumns ? idx < numberOfColumns : true));
};

const StyledTabAnchorTag = styled.a`
  &:hover {
    text-decoration: none;
  }
`;

// Type/shape dispatch for renderDisplayField's post-BOOL/empty-value display transform.
// Guard-clause conversion of the original's if/else-if chain: each condition is checked
// independently in the same order, so first-match-wins semantics are preserved exactly, and the
// trailing `return value;` is REQUIRED -- most AddlFieldType values (NUM, STR, TEXT, DATE,
// RICH_TEXT, DUMMY, ...) match none of the six branches and must pass through unchanged.
function isObjectRelationType(fieldType) {
  return (
    fieldType === AddlFieldType.OBJECT_RELATION ||
    fieldType === AddlFieldType.OBJECT_RELATION_MULTI
  );
}

function hasDisplayFieldsDict(value) {
  return (
    typeof value === 'object' && value.hasOwnProperty('_display_fields_dict')
  );
}

function isArrayLikeValue(value) {
  return Boolean(value) && value.constructor === Array;
}

function computeDisplayValue(value, fieldConfig, entity) {
  if (fieldConfig.field_type === AddlFieldType.HYPERLINK) {
    return (
      <StyledTabAnchorTag href={value} target="_blank">
        {value}
      </StyledTabAnchorTag>
    );
  }
  if (fieldConfig.field_type === AddlFieldType.TAG && Array.isArray(value)) {
    return value.map((tag) => tag.value).join(', ');
  }
  if (hasDisplayFieldsDict(value)) {
    return value._display_fields_dict.plain_text_value;
  }
  if (isArrayLikeValue(value)) {
    return value
      .map((item) => item?._display_fields_dict?.plain_text_value ?? item)
      .join(', ');
  }
  if (isObjectRelationType(fieldConfig.field_type)) {
    return entity[`${fieldConfig.name}_to_display_text`];
  }
  if (fieldConfig.field_type === AddlFieldType.DATETIME) {
    return convertDateFromServer(value);
  }
  return value;
}

export const renderDisplayField = (entity, fieldConfig, options = {}) => {
  const { as: Wrapper } = options;

  let value = entity[fieldConfig.name];
  if (fieldConfig.name === 'status') {
    value = entity.status ?? entity.state;
  }
  if (fieldConfig.field_type === AddlFieldType.BOOL) {
    value = value === true ? 'YES' : 'NO';
  } else if (!value || (Array.isArray(value) && !value.length)) return '-';

  value = computeDisplayValue(value, fieldConfig, entity);

  return Wrapper ? <Wrapper>{value}</Wrapper> : <strong>{value}</strong>;
};

// classifyDataType/formatArrayValue/formatObjValue: returnString's own dataType-detection
// ternary chain and its switch's array/obj cases, extracted to their own module-scope helpers
// (returnString was independently CCN-flagged, not just nested inside testResultsRender --
// hoisting it alone does not reduce its own branching, so its internals need splitting too).
function classifyDataType(data) {
  if (!data) return '';
  if (data.constructor === Array) return 'array';
  if (data.constructor === Object) return 'obj';
  if (data.constructor === String) return 'string';
  return '';
}

function formatArrayValue(data) {
  return data.length ? data.map((item) => item.value).join(', ') : '';
}

function formatObjValue(data) {
  if (data.value) return data.value;
  if (data._display_fields_dict)
    return data._display_fields_dict.plain_text_value;
  return '';
}

// PRESERVED PRE-EXISTING BEHAVIOR (not fixed here): the default case reads the literal string
// key 'showString', not the showString parameter (row[showString]) -- almost certainly a
// pre-existing bug (the parameter is otherwise unused here).
function returnString(row, data, showString) {
  const dataType = classifyDataType(data);
  switch (dataType) {
    case 'string':
      return data;
    case 'array':
      return formatArrayValue(data);
    case 'obj':
      return formatObjValue(data);
    default:
      return row['showString'];
  }
}

// resolveResultField unifies testResultsRender's two structurally-identical TEST_FIELD/
// WORKSHEET_FIELD/default switches. `resultFieldName` is the RAW (unaliased) field name, used
// for both getFieldConfig and the WORKSHEET_FIELD lookup in BOTH branches (neither branch
// aliases there); `rawLookupFieldName` is used ONLY by TEST_FIELD's returnString fallback, the
// one place the original's status->state aliasing applies (and only in the portalView branch).
function resolveResultField(
  row,
  resultFieldType,
  resultFieldName,
  rawLookupFieldName,
) {
  switch (resultFieldType) {
    case 'TEST_FIELD': {
      const fieldConfig = getFieldConfig('TEST', resultFieldName);
      if (fieldConfig) return renderDisplayField(row, fieldConfig);
      return returnString(row, row?.[rawLookupFieldName], 'results');
    }
    case 'WORKSHEET_FIELD': {
      if (
        row.worksheet_data &&
        row.worksheet_data.hasOwnProperty(resultFieldName)
      ) {
        return returnString(
          row,
          row.worksheet_data?.[resultFieldName]?.value,
          'results',
        );
      }
      return row.state;
    }
    default:
      return row.results;
  }
}

export const testResultsRender = (value, row) => {
  if (!row.release_results) return '-';
  if (row.assay.cp_result_field && row.assay.cp_result_field_type) {
    const name = row.assay.cp_result_field;
    return resolveResultField(row, row.assay.cp_result_field_type, name, name);
  }
  const portalView = JSON.parse(
    localStorage.getItem('portalView'),
  )?.test_results_field;
  const rawName = portalView?.cp_result_field;
  const aliasedName = rawName === 'status' ? 'state' : rawName;
  return resolveResultField(
    row,
    portalView?.cp_result_field_type,
    rawName,
    aliasedName,
  );
};

// Field-type dispatch table for CreateOrderCommonContentToString's non-relationship branches.
// A resolver with no matching entry falls back to `sampleFieldName` unchanged -- required,
// since the original's final `else` covers the majority of field_types.
const FIELD_TYPE_VALUE_RESOLVERS = {
  SELECT: (sampleFieldName) => showSelectedValue(sampleFieldName),
  SELECT_MULTI: (sampleFieldName) =>
    sampleFieldName.map((ele) => showSelectedValue(ele)).join(','),
  DATETIME: (sampleFieldName) => convertDateFromServer(sampleFieldName),
};

export const CreateOrderCommonContentToString = (
  hidden,
  sampleFieldName,
  fieldName,
  sampleMeta,
  fieldConfig,
) => {
  if (hidden || !sampleFieldName) return undefined;
  if (Object.keys(sampleMeta.relationshipFields).includes(fieldName)) {
    return sampleMeta.relationshipFields[fieldName].getOptionLabel(
      sampleFieldName,
    );
  }
  // hasOwnProperty guard: a field_type that happens to collide with an inherited
  // Object.prototype member name (e.g. 'toString', 'constructor') would otherwise resolve to
  // that inherited function via a bare bracket lookup instead of falling through to the
  // pass-through default the original else-if chain's final `else` branch gave every
  // unmatched field_type.
  const resolver = Object.prototype.hasOwnProperty.call(
    FIELD_TYPE_VALUE_RESOLVERS,
    fieldConfig.field_type,
  )
    ? FIELD_TYPE_VALUE_RESOLVERS[fieldConfig.field_type]
    : undefined;
  return resolver ? resolver(sampleFieldName) : sampleFieldName;
};

const showSelectedValue = (data) => {
  if (typeof data === 'object') {
    return data.label;
  } else {
    return data;
  }
};

/**
 * Keep for backward compatibilty until V1 is deprecated.
 */
const entityRefCoreBase = {
  [EntityType.PROJECT]: {
    getOptionLabel: (obj) => obj.title,
    getOptionValue: (obj) => obj.id,
    getDefaultValue: parseObjRelDefault,
    creatable: {
      mutation: useProjectCreate,
      policy: policies.ui.projectDropdownCreateBtn,
    },
  },
  [EntityType.TURNAROUND]: {
    getOptionLabel: (obj) => obj.name,
    getOptionValue: (obj) => obj.id,
    getDefaultValue: (obj) =>
      parseObjRelDefault(obj, {
        value: null,
        label: 'Standard',
      }),
  },
};
const entityRefAddlBase = {
  [EntityType.USER]: {
    getOptionLabel: (obj) =>
      obj._display_fields_dict?.plain_text_value || obj.value || obj.label,
    getOptionValue: (obj) => obj.id || obj.value,
    getDefaultValue: parseObjRelDefault,
  },
  [EntityType.CONTACT]: {
    getOptionLabel: (obj) => obj._display_fields_dict?.plain_text_value,
    getOptionValue: (obj) => obj.id,
    getDefaultValue: parseObjRelDefault,
  },
};
export const entityRefMap = {
  [EntityType.ORDER]: {
    [EntityType.CUSTOMER]: {
      getOptionLabel: (obj) =>
        obj.customer_name || obj.customer_account?.customer_name,
      getOptionValue: (obj) => obj.id || obj.customer_account_id,
      getDefaultValue: parseObjRelDefault,
    },
    [EntityType.TURNAROUND]: entityRefCoreBase[EntityType.TURNAROUND],
    [EntityType.PROJECT]: entityRefCoreBase[EntityType.PROJECT],
    ...entityRefAddlBase,
  },
  [EntityType.SAMPLE]: {
    [EntityType.PROJECT]: entityRefCoreBase[EntityType.PROJECT],
    [EntityType.SOURCE]: {
      getOptionLabel: (obj) => obj.display_name,
      getOptionValue: (obj) => obj.id,
      getDefaultValue: parseObjRelDefault,
      creatable: {
        mutation: useSourceUpsert,
      },
    },
    [EntityType.ACCESSIONING_TYPE]: {
      getOptionLabel: (obj) => obj.value,
      getOptionValue: (obj) => obj.id,
      getDefaultValue: parseObjRelDefault,
    },
    ...entityRefAddlBase,
  },
  [EntityType.TEST]: {
    [EntityType.TURNAROUND]: entityRefCoreBase[EntityType.TURNAROUND],
    ...entityRefAddlBase,
  },
  [EntityType.SOURCE]: {
    [EntityType.PROJECT]: entityRefCoreBase[EntityType.PROJECT],
    ...entityRefAddlBase,
  },
  [EntityType.CONTACT]: entityRefAddlBase,
  [EntityType.CUSTOMER]: entityRefAddlBase,
};
